import memoization from "@services/memoization";
import StorageUtils from "@services/storage";
import * as PafAPI from "@utils/apis/paf/endpoints";
import * as API from "@utils/apis/wbs/endpoints";
import { convertDateToServerDate } from "@utils/date";
import type { AxiosRequestConfig } from "axios";
import { store } from "../../../store";
import { createApiDispatchers } from "../../apiReducer";
import { dispatchUILoaded, dispatchUILoading } from "../../uiReducer";
import type * as APIInterface from "./interface";

const { dispatchSetApi, dispatchErrorApi, dispatchStartApi } =
	createApiDispatchers(store.dispatch);

export const addressSearchWrapper: APIInterface.AddressSearchWrapper = async (
	input,
) => {
	const { postcode } = input;

	if (!postcode) {
		throw new Error("Postcode is required");
	}

	try {
		const response = await PafAPI.getAddressesByPostcode({ postcode });

		if (!response) {
			return [];
		}

		const sanitizedAddresses = response.map((address) => ({
			...address,
			buildingNumber: undefined, // no longer needed
			buildingName: undefined, // no longer needed
			subBuildingName: undefined, // no longer needed
			addressObj: {
				...address.addressObj,
				premise: address.addressObj?.premise || "N/A",
				cache: undefined,
			},
		}));

		const sortedAddresses = sanitizedAddresses.sort((a, b) => {
			if (a.addressString && b.addressString) {
				return a.addressString.localeCompare(b.addressString);
			}

			// Handle cases where addressString might be undefined or null
			if (!a.addressString && !b.addressString) return 0;
			return a.addressString ? 1 : -1;
		});

		return sortedAddresses;
	} catch (error: any) {
		if (error?.response?.data) {
			const { error: apiError, message } = error.response.data;
			return { error: apiError, message };
		}
	}
};

const apiMap: APIInterface.MapOfAPIEndpoints = {
	AccountCode: {
		name: "AccountCode",
		method: API.tablesAccountCodesGetByAccountCode,
	},
	AccountCodeNoOverride: {
		name: "AccountCodeNoOverride",
		method: API.tablesAccountCodesGetByAccountCode,
	},
	StripePublicKey: {
		name: "StripePublicKey",
		method: API.paymentsStripeGetPublicKey,
	},
	StripeSetupIntent: {
		name: "StripeSetupIntent",
		method: API.stripeCreateSetupIntent,
	},
	StripePaymentMethod: {
		name: "StripePaymentMethod",
		method: API.paymentsStripeGetPaymentMethod,
	},
	SquareLocationId: {
		name: "SquareLocationId",
		method: API.paymentsSquareGetLocationId,
	},
	ChargingTableBaseVAT: {
		name: "ChargingTableBaseVAT",
		method: API.tablesChargingTableBaseVATGetAll,
	},
	ChargingTablePrices: {
		name: "ChargingTablePrices",
		method: API.tablesChargingTablePricesGetAll,
	},
	ChargingTableBase: {
		name: "ChargingTableBase",
		method: API.tablesChargingTableBaseGetAll,
	},
	ChargingTablePriceAdjustments: {
		name: "ChargingTablePriceAdjustments",
		method: API.tablesChargingTablePriceAdjustmentsGetAll,
	},
	ChargingTableAccountTypes: {
		name: "ChargingTableAccountTypes",
		method: API.tablesChargingTableAccountTypesGetAll,
	},
	ChargingTableAccountCodes: {
		name: "ChargingTableAccountCodes",
		method: API.tablesChargingTableAccountCodesGetAll,
	},
	ChargingTablePostcodes: {
		name: "ChargingTablePostcodes",
		method: API.tablesChargingTablePostcodesGetAll,
	},
	ChargingTableScheduleGroups: {
		name: "ChargingTableScheduleGroups",
		method: API.tablesChargingTableScheduleGroupsGetAll,
	},
	ChargingTableScheduleGroupSchedules: {
		name: "ChargingTableScheduleGroupSchedules",
		method: API.tablesChargingTableScheduleGroupSchedulesGetAll,
	},
	Agents: { name: "Agents", method: API.puzzelAgentsGetAll },
	AppointmentTimeTypes: {
		name: "AppointmentTimeTypes",
		method: API.tablesAppointmentTimeTypesGetAll,
	},
	FailedReasons: {
		name: "FailedReasons",
		method: API.tablesFailedReasonsGetAll,
	},
	OtherPhoneCalls: {
		name: "OtherPhoneCalls",
		method: API.tablesOtherPhoneCallsGetAll,
	},
	BookingRestrictions: {
		name: "BookingRestrictions",
		method: API.storedProceduresBookingGetBookingRestrictions,
	},
	CompanyProfile: {
		name: "CompanyProfile",
		method: API.companiesHouseGetCompanyDetails,
	},
	CallTypes: { name: "CallTypes", method: API.tablesCallTypesGetAll },
	CallEvents: { name: "CallEvents", method: API.puzzelCallEventsGetBySource },
	CallHistory: { name: "CallsHistory", method: API.puzzelEventsGetBySource },
	JobScripts: { name: "JobScripts", method: API.tablesJobScriptsGetAll },
	PostcodeAreas: {
		name: "PostcodeAreas",
		method: API.tablesPostcodeAreasGetAll,
	},
	Top5Calls: { name: "Top5Calls", method: API.webBookingGetTop5Calls },
	GetCurrentCall: {
		name: "GetCurrentCall",
		method: API.storedProceduresCallsGetCurrentCall,
	},
	GetCurrentCallFive9: {
		name: "GetCurrentCallFive9",
		method: (body: { agentId: number; callId: number }) =>
			API.storedProceduresCallsGetCurrentCallFive9(body.callId, body.agentId),
	},
	GetAddressBlacklist: {
		name: "AddressBlacklist",
		method: API.tablesAddressBlacklistGetAll,
	},
	Charges: { name: "Charges", method: API.tablesChargesGetAll },
	GetVATRates: { name: "VATRates", method: API.tablesVATRatesGetAll },
	LandlordAddresses: { name: "LandlordAddresses", method: API.vwPAFAddress },
	PropertyAddresses: { name: "PropertyAddresses", method: API.vwPAFAddress },
	ServiceAgreementCustomerAddresses: {
		name: "ServiceAgreementCustomerAddresses",
		method: API.vwPAFAddress,
	},
	ServiceAgreementPolicyAddresses: {
		name: "ServiceAgreementPolicyAddresses",
		method: API.vwPAFAddress,
	},
	ServiceAgreementBillingAddresses: {
		name: "ServiceAgreementBillingAddresses",
		method: API.vwPAFAddress,
	},
	ServiceAgreementDeclinedReason: {
		name: "ServiceAgreementDeclinedReason",
		method: API.tablesServiceAgreementDeclinedReasonGetAll,
	},
	ServiceAgreementCustomer: {
		name: "ServiceAgreementCustomer",
		method: API.storedProceduresServiceAgreementsAPIGetCustomerMatches,
	},
	SetServiceAgreementCustomer: {
		name: "SetServiceAgreementCustomer",
		method: API.storedProceduresServiceAgreementsSetCustomer,
	},
	ServiceAgreementInfo: {
		name: "ServiceAgreementInfo",
		method: API.tablesServiceAgreementsGetInfoById,
	},
	BookingPaymentAddresses: {
		name: "BookingPaymentAddresses",
		method: API.vwPAFAddress,
	},
	ServiceAgreementByJobId: {
		name: "ServiceAgreementByJobId",
		method: API.storedProceduresServiceAgreementsGetServiceAgreementForJob,
	},
	ServiceAgreementPlanAddresses: {
		name: "ServiceAgreementPolicyAddresses",
		method: API.vwPAFAddress,
	},
	CurrentDestNumber: {
		name: "CurrentDestNumber",
		method: API.puzzelPhoneNumbersGetById,
	},
	GlobalSettings: {
		name: "GlobalSettings",
		method: API.tablesGlobalSettingsGetAll,
	},
	DestinationNumber: {
		name: "DestinationNumber",
		method: API.puzzelEventsGetBySessionId,
	},
	CallMeBackAuth: {
		name: "CallMeBackAuth",
		method: API.storedProceduresUsersManagersAuthorisation,
	},
	DiscountTypes: {
		name: "DiscountTypes",
		method: API.storedProceduresDiscountsGetDiscounts,
	},
	GroupTypes: { name: "GroupTypes", method: API.tablesJobTypeGroupsGetAll },
	JobTypes: { name: "JobTypes", method: API.tablesJobTypesGetAll },
	Last2ByLan: { name: "Last2ByLan", method: API.jobsGetLast2JobsByLandline },
	Last2ByMob: { name: "Last2ByMob", method: API.jobsGetLast2JobsByMobile },
	Last2: { name: "Last2", method: API.jobsFindRecentByCustomer },
	CompletedJobs: {
		name: "CompletedJobs",
		method: API.tablesCompletionsGetByJobId,
	},
	JobById: { name: "JobById", method: API.jobsGetById },
	JobReviews: { name: "JobReviews", method: API.tablesJobReviewsGetByJobId },
	LastJob: { name: "LastJob", method: API.storedProceduresBookingGetLastJob },
	ProblemAreaCode: {
		name: "ProblemAreaCode",
		method: API.postcodeGridReferenceGet,
	},
	ProblemNewAreaCode: {
		name: "ProblemNewAreaCode",
		method: API.postcodeDistrictGridReferenceGet,
	},
	ResponseTimes: {
		name: "ResponseTimes",
		method: API.tablesResponseTimesGetAll,
	},
	SetBookingId: {
		name: "SetBookingId",
		method: API.storedProceduresJobsBookingGetId,
	},
	SetData: {
		name: "SetData",
		method: API.storedProceduresTrackFastDataproviderSetData,
	},
	SetupBooking: {
		name: "SetupBooking",
		method: API.storedProceduresTrackFastJobsSetupBooking,
	},
	SetCharge: { name: "SetCharge", method: API.storedProceduresJobsSetCharge },
	GetSetting: {
		name: "GetSetting",
		method: API.storedProceduresSettingsGetSetting,
	},
	TimeRequirementsTypes: {
		name: "TimeRequirementsTypes",
		method: API.storedProceduresTimeRequirementTypesGet,
	},
	TradeGroupsMap: {
		name: "TradeGroupsMap",
		method: API.storedProceduresTrackFastJobsLookupGetJobGroups,
	},
	TradeTypes: {
		name: "TradeTypes",
		method: API.storedProceduresTrackFastJobsLookupGetJobTypes,
	},
	Events: { name: "Events", method: API.puzzelEventsGetBySessionId },
	EventsByCallId: {
		name: "EventsByCallId",
		method: API.puzzelEventsGetByCallId,
	},
	UpdateBooking: {
		name: "UpdateBooking",
		method: API.storedProceduresTrackFastJobsUpdateJob,
	},
	GetCharges: {
		name: "Charge",
		method: API.storedProceduresTrackFastJobsGetChargesBooking,
	},
	GetScript: {
		name: "Script",
		method: API.storedProceduresJobScriptsGetScript,
	},
	InitialBooking: {
		name: "InitialBooking",
		method: API.storedProceduresJobsInitialBooking,
	},
	GetServiceAgreementsAvailability: {
		name: "ServiceAgreementsAvailability",
		method: API.storedProceduresServiceAgreementsCheckAvailability,
	},
	GetServiceAgreementsValidateCampaignCode: {
		name: "ServiceAgreementsValidateCampaignCode",
		method: API.storedProceduresServiceAgreementsValidateCampaignCode,
	},
	GetServiceAgreementsGetPlanCostAfterOffers: {
		name: "ServiceAgreementsGetPlanCostAfterOffers",
		method: API.storedProceduresServiceAgreementsGetPlanCostAfterOffers,
	},
	GetServiceAgreementsGetPlanCostAfterOffersV2: {
		name: "ServiceAgreementsGetPlanCostAfterOffersV2",
		method: API.storedProceduresServiceAgreementsGetPlanCostAfterOffersV2,
	},
	GetCallMeBack: {
		name: "GetCallMeBack",
		method: API.storedProceduresCallMeBacksGetCallMeBack,
	},
	GetCallMeBackNotes: {
		name: "GetCallMeBackNotes",
		method: API.storedProceduresCallMeBacksGetNotes,
	},
	AllocationQueues: {
		name: "AllocationQueues",
		method: API.storedProceduresTrackFastEngineersQueuesAddJobToAllocationQueue,
	},
	GetBoilerManufacturers: {
		name: "BoilerManufacturers",
		method: API.storedProceduresBoilersGetManufacturers,
	},
	GetHeatingTypes: {
		name: "HeatingTypes",
		method: API.storedProceduresBoilersGetHeatingTypes,
	},
	GetBuildingTypes: {
		name: "BuildingTypes",
		method: API.storedProceduresPropertiesGetBuildingTypes,
	},
	GetBenefitsTypes: {
		name: "BenefitsTypes",
		method: API.storedProceduresServiceAgreementsGetTypeBenefits,
	},
	SetRequisition: {
		name: "Requisition",
		method: API.storedProceduresTrackFastJobsSendRequisition,
	},
	GetEnquiriesQueue: {
		name: "EnquiriesQueue",
		method: API.storedProceduresEnquiriesQueuesGetQueue,
	},
	GetServiceAgreementType: {
		name: "ServiceAgreementType",
		method: API.storedProceduresServiceAgreementsGetType,
	},
	GetServiceAgreementTypes: {
		name: "ServiceAgreementTypes",
		method: API.storedProceduresServiceAgreementsGetTypes,
	},
	GetIncentiveTypes: {
		name: "IncentiveTypes",
		method: API.storedProceduresServiceAgreementsGetIncentiveTypes,
	},
	GetUsersStatistics: {
		name: "UsersStatistics",
		method: API.storedProceduresUsersGetStatistics,
	},
	GetIncentive: {
		name: "Incentive",
		method: API.storedProceduresServiceAgreementsGetIncentive,
	},
	GetAvailableOffers: {
		name: "AvailableOffers",
		method: API.storedProceduresServiceAgreementsGetAvailableOffers,
	},
	GetAvailableOffersV2: {
		name: "AvailableOffersV2",
		method: API.storedProceduresServiceAgreementsGetAvailableOffersV2,
	},
	GetOfferAllocationReasons: {
		name: "OfferAllocationReasons",
		method: API.storedProceduresServiceAgreementsGetOfferAllocationReasons,
	},
	GetPaymentTypes: {
		name: "PaymentTypes",
		method: API.storedProceduresServiceAgreementsGetPaymentTypes,
	},
	SetServiceAgreementV4: {
		name: "ServiceAgreementV4",
		method: API.storedProceduresServiceAgreementsSetServiceAgreementV4,
	},
	SetServiceAgreementV5: {
		name: "ServiceAgreementV5",
		method: API.storedProceduresServiceAgreementsSetServiceAgreementV5,
	},
	CallMeBacksSetAsBooked: {
		name: "CallMeBacksSetAsBooked",
		method: API.storedProceduresCallMeBacksSetAsBooked,
	},
	GetAutoAllocation: {
		name: "AutoAllocation",
		method: API.storedProceduresAutoAllocationGetLookup,
	},
	GetPaymentMethods: {
		name: "PaymentMethods",
		method: API.storedProceduresTrackFastJobsLookupGetPaymentMethods,
	},
	CheckAddressBlacklist: {
		name: "AddressBlacklist",
		method: API.storedProceduresTrackFastAddressBlacklistCheck,
	},
	SetCall: { name: "Call", method: API.tablesCallsCreate },
	PatchJob: { name: "PatchJob", method: API.jobsPatchJob },
	PatchCalls: { name: "PatchCalls", method: API.tablesCallsPatch },
	AddressSearch: { name: "AddressSearch", method: addressSearchWrapper },
	JobsSearch: {
		name: "JobsSearch",
		method: API.storedProceduresTrackFastSearchGetSearchResult,
	},
	JobDetails: {
		name: "JobDetails",
		method: API.storedProceduresJobsGetJobDetails,
	},
	JobCompletion: {
		name: "JobCompletion",
		method: API.storedProceduresJobsGetJobDetails,
	},
	CallByJobId: { name: "CallByJobId", method: API.tablesCallsGetByJobId },
	CallTypesSp: {
		name: "CallTypesSp",
		method: API.storedProceduresTrackFastJobsLookupGetCallTypes,
	},
	CallMeBackSendSMS: {
		name: "CallMeBackSendSMS",
		method: API.storedProceduresCallMeBacksSendSMS,
	},
	CallMeBackSetCalled: {
		name: "CallMeBackSetCalled",
		method: API.storedProceduresCallMeBacksSetCalled,
	},
	CallMeBackSendEmail: {
		name: "CallMeBackSendEmail",
		method: API.storedProceduresCallMeBacksSendEmail,
	},
	GetCallMeSetFollowUp: {
		name: "GetCallMeSetFollowUp",
		method: API.storedProceduresCallMeBacksSetFollowUp,
	},
	GetCallMeBackQueue: {
		name: "GetCallMeBackQueue",
		method: API.storedProceduresCallMeBacksQueuesGetQueue,
	},
	GetCallMeBackLock: {
		name: "GetCallMeBackLock",
		method: API.storedProceduresTrackFastLockingGetLock,
	},
	GetCallMeBackUnlock: {
		name: "GetCallMeBackUnlock",
		method: API.storedProceduresTrackFastLockingRemoveLock,
	},
	GetCallMeBackCancel: {
		name: "GetCallMeBackCancel",
		method: API.storedProceduresCallMeBacksCancelEnquiry,
	},
	SetCallMeBackLock: {
		name: "SetCallMeBackLock",
		method: API.storedProceduresTrackFastLockingAddLock,
	},
	BookingDataGetAll: {
		name: "BookingDataGetAll",
		method: API.webBookingDataGetAll,
	},
	BookingDataGet: { name: "BookingDataGet", method: API.webBookingDataGetById },
	BookingDataPost: {
		name: "BookingDataPost",
		method: API.webBookingDataCreate,
	},
	BookingDataPatch: {
		name: "BookingDataPut",
		method: API.webBookingDataUpdate,
	},
	BookingDataDelete: {
		name: "BookingDataDelete",
		method: API.webBookingDataGetVersions,
	},
	BookingDataGetAllVersions: {
		name: "BookingDataGetAllVersions",
		method: API.webBookingDataGetVersions,
	},
	ApiLogs: { name: "ApiLogs", method: API.webApiLogGetAll },
	ApiLogsById: { name: "ApiLogsById", method: API.webApiLogGetById },
	ApiLogsCreate: { name: "ApiLogsCreate", method: API.webApiLogCreate },
	ApiLogsCreateMany: {
		name: "ApiLogsCreateMany",
		method: API.webApiLogCreateMany,
	},
	ApiLogsDelete: { name: "ApiLogsDelete", method: API.webApiLogDelete },
	GeneralLogs: { name: "GeneralLogs", method: API.webGeneralLogGetAll },
	GeneralLogsById: {
		name: "GeneralLogsById",
		method: API.webGeneralLogGetById,
	},
	GeneralLogsCreate: {
		name: "GeneralLogsCreate",
		method: API.webGeneralLogCreate,
	},
	GeneralLogsCreateMany: {
		name: "GeneralLogsCreateMany",
		method: API.webGeneralLogCreateMany,
	},
	GeneralLogsDelete: {
		name: "GeneralLogsDelete",
		method: API.webGeneralLogDelete,
	},
	GoCardlessBankAccount: {
		name: "GoCardlessBankAccount",
		method: API.postBankAccount,
	},
	GoCardlessCustomer: { name: "GoCardlessCustomer", method: API.postCustomer },
};

const memoMap: APIInterface.MemoizationMap = [
	{ name: "AccountCode", uniqueParams: ["accountCode"] }, //verified
	{ name: "AddressSearch", uniqueParams: "postcode" }, //verified
	{ name: "CompletedJobs", uniqueParams: "any" }, //verified
	{
		name: "GetCharges",
		uniqueParams: [
			"callTypeId",
			"accountCode",
			"postcode",
			"jobTypeCode",
			"dateTimeRequired",
			"quantity",
			"options",
			"isCommercial",
			"discountedRate",
		],
	}, //verified
	{
		name: "GetServiceAgreementsAvailability",
		uniqueParams: ["postcode", "boilerAge", "jobTypeCode"],
	}, //verified
	{
		name: "GetServiceAgreementsGetPlanCostAfterOffers",
		uniqueParams: [
			"allocationContractPeriod",
			"contractPeriodMonths",
			"paymentFrequencyMonths",
			"offerParams",
			"serviceAgreementTypeId",
		],
	}, //verified
	{ name: "GetAvailableOffers", uniqueParams: ["serviceAgreementTypeId"] }, //verified
	{ name: "JobReviews", uniqueParams: ["jobId"] },
	{ name: "JobDetails", uniqueParams: ["JobId"] }, //verified
	{ name: "JobCompletion", uniqueParams: ["jobId"] }, //verified
	{ name: "JobsSearch", uniqueParams: ["hours", "search"] }, //verified
	{ name: "JobById", uniqueParams: "any" }, //verified
	{
		name: "Last2",
		uniqueParams: [
			"postcode",
			"property",
			"telephoneMobile",
			"telephoneLandline",
			"emailAddress",
		],
	}, //verified
	{
		name: "LastJob",
		uniqueParams: ["postcode", "property", "jobId", "tradeCode"],
	}, //verified
	{ name: "PropertyAddresses", uniqueParams: "postcode" },
	{
		name: "ServiceAgreementCustomer",
		uniqueParams: [
			"postCode",
			"surname",
			"forename",
			"phoneLandline",
			"phoneMobile",
			"email",
		],
	}, //verified
];

// ignore list for calls to the server which should not trigger spinner
const ignoreList = [
	"GetCharges",
	"LastJob",
	"GetAvailableOffers",
	"GetServiceAgreementsGetPlanCostAfterOffers",
	"GetServiceAgreementsAvailability",
];

const cachedAPI: APIInterface.CachedAPI = async ({ name, id, body, path }) => {
	// spinner can be disabled temporarily by external call
	const spinner = StorageUtils.getFromStorage({
		type: "session",
		store: "temp",
		key: "spinner",
	});

	// if name is not in the ignore list, show spinner - we do not want to show spinner some queries
	if (!ignoreList.includes(String(name)) || !spinner.disabled) {
		// ui.Loading = true; this will display the spinner
		dispatchUILoading();
	}

	// store.dispatch({ type: "fetchAPI", payload: "", name: "", error: "", body: null });
	dispatchStartApi();

	const memoizedCall = memoMap.find((memo) => memo.name === name);
	const axiosParams: (number | string | AxiosRequestConfig<any>)[] = [];

	try {
		let response: any;
		body = convertDateToServerDate(body);
		id && axiosParams.push(id);
		body && axiosParams.push(body);
		path && axiosParams.push(path);

		// if memoMap has a match, use the memoized version of the call
		if (memoizedCall) {
			const callback = () => apiMap[name].method(...axiosParams);
			// memoizedCall.uniqueParam are used to determine if the call is unique therefore
			// if the call is unique, the memoized version of the call is used, otherwise the api is used
			// if parameters are array of strings, parameters are used to determine if the call is unique
			// if parameters are a single string, it is used to determine if the call is unique
			let uniqueParams = "";
			if (Array.isArray(memoizedCall.uniqueParams)) {
				uniqueParams = memoizedCall.uniqueParams
					.map((param) => {
						if (body && body[param] && typeof body[param] === "object") {
							return JSON.stringify(body[param])
								.replace(/"/g, "") // remove quotes
								.replace(/,/g, "") // remove commas
								.replace(/:/g, "") // remove colons
								.replace(/ /g, "") // remove spaces
								.replace(/\\/g, "") // remove backslashes
								.replace(/}/g, "") // remove closing braces
								.replace(/{/g, "") // remove opening braces
								.replace(/\[/g, "") // remove opening brackets
								.replace(/]/g, ""); // remove closing brackets
						}
						return String(body[param]).replaceAll(" ", "");
					})
					.join("-");
			} else {
				if (memoizedCall.uniqueParams === "any") {
					uniqueParams = String(axiosParams).replaceAll(" ", "");
				} else {
					uniqueParams = String(body[memoizedCall.uniqueParams]).replaceAll(
						" ",
						"",
					);
				}
			}
			response = await memoization.get(
				`${memoizedCall.name}-${uniqueParams}`,
				callback,
			);
			// if memoMap has no match, use the normal version of the call
		} else {
			response = await apiMap[name].method(...axiosParams);
		}
		dispatchSetApi(apiMap[name].name, response, body);

		// if name is not in the ignore list, hide spinner
		if (!ignoreList.includes(String(name)) || !spinner.disabled) {
			// ui.Loading = false;
			dispatchUILoaded();
		}
		return response;
	} catch (error: any) {
		if (memoizedCall && error.response?.status === 404) {
			let uniqueParams = "";
			if (Array.isArray(memoizedCall.uniqueParams)) {
				uniqueParams = memoizedCall.uniqueParams
					.map((param) => String(body[param]).replaceAll(" ", ""))
					.join("-");
			} else {
				if (memoizedCall.uniqueParams === "any") {
					uniqueParams = String(axiosParams).replaceAll(" ", "");
				} else {
					uniqueParams = String(body[memoizedCall.uniqueParams]).replaceAll(
						" ",
						"",
					);
				}
			}
			memoization.set(`${memoizedCall.name}-${uniqueParams}`, undefined);
			// if memoMap has no match, use the normal version of the call
		}
		dispatchErrorApi();
		//if name is not in the ignore list, hide spinner
		if (!ignoreList.includes(String(name))) {
			// ui.Loading = false;
			dispatchUILoaded();
		}
		return;
	}
};

export const api: APIInterface.Api = () => {
	// const fetchAPI: APIInterface.fetchAPI = async ({ name, body, id, path }) => fetchAPI({ name, body, id, path });
	return {
		async fetchAccountCode(body) {
			return cachedAPI({ name: "AccountCode", body });
		},
		async fetchAccountCodeNoOverride(body) {
			return cachedAPI({ name: "AccountCodeNoOverride", body });
		},
		async fetchStripePublicKey(body) {
			return cachedAPI({ name: "StripePublicKey", body });
		},
		async fetchStripSetupIntent(body) {
			return cachedAPI({ name: "StripeSetupIntent", body });
		},
		async fetchStripePaymentMethod(body) {
			return cachedAPI({ name: "StripePaymentMethod", body });
		},
		async fetchSquareLocationId(body) {
			return cachedAPI({ name: "SquareLocationId", body });
		},
		async fetchAddresses(user, body) {
			return cachedAPI({
				name: `${
					user as
						| "BookingPayment"
						| "Landlord"
						| "Property"
						| "ServiceAgreementBilling"
						| "ServiceAgreementCustomer"
						| "ServiceAgreementPlan"
						| "ServiceAgreementPolicy"
				}Addresses`,
				body,
			});
		},
		async fetchAgents() {
			return cachedAPI({ name: "Agents" });
		},
		async fetchGetAddressBlacklist() {
			return cachedAPI({ name: "GetAddressBlacklist" });
		},
		async fetchLastJob(body) {
			return cachedAPI({ name: "LastJob", body });
		},
		async fetchAppointmentTimeTypes() {
			return cachedAPI({ name: "AppointmentTimeTypes" });
		},
		async fetchFailedReasons() {
			return cachedAPI({ name: "FailedReasons" });
		},
		async fetchVATRates() {
			return cachedAPI({ name: "GetVATRates" });
		},
		async fetchBookingRestrictions() {
			return cachedAPI({ name: "BookingRestrictions" });
		},
		async fetchCompanyProfile(path) {
			return cachedAPI({ name: "CompanyProfile", path });
		},
		async fetchCallTypes() {
			return cachedAPI({ name: "CallTypes" });
		},
		async fetchCallEventsBySource(body) {
			return cachedAPI({ name: "CallEvents", body });
		},
		async fetchCharges() {
			return cachedAPI({ name: "Charges" });
		},
		async fetchJobsSetCharge(body) {
			return cachedAPI({ name: "SetCharge", body });
		},
		async fetchServiceAgreementByJobId(body) {
			return cachedAPI({ name: "ServiceAgreementByJobId", body });
		},
		async fetchCurrentDestNumberByNumber(body) {
			return cachedAPI({ name: "CurrentDestNumber", body });
		},
		async fetchCheckCallMeBackAuthorization(body) {
			return cachedAPI({ name: "CallMeBackAuth", body });
		},
		async fetchDestinationNumberBySessionId(body) {
			return cachedAPI({ name: "DestinationNumber", body });
		},
		async fetchDiscountTypes(body) {
			return cachedAPI({ name: "DiscountTypes", body });
		},
		async fetchGroupTypes() {
			return cachedAPI({ name: "GroupTypes" });
		},
		async fetchServiceAgreementDeclinedReason() {
			return cachedAPI({ name: "ServiceAgreementDeclinedReason" });
		},
		async fetchServiceAgreementCustomer(body) {
			return cachedAPI({ name: "ServiceAgreementCustomer", body });
		},
		async fetchServiceAgreementInfo(body) {
			return cachedAPI({ name: "ServiceAgreementInfo", body });
		},
		async fetchJobTypes() {
			return cachedAPI({ name: "JobTypes" });
		},
		async fetchLast2ByLan(body) {
			return cachedAPI({ name: "Last2ByLan", body });
		},
		async fetchLast2ByMob(body) {
			return cachedAPI({ name: "Last2ByMob", body });
		},
		async fetchLast2(body) {
			return cachedAPI({ name: "Last2", body });
		},
		async fetchPropertyAddresses(body) {
			return cachedAPI({ name: "PropertyAddresses", body });
		},
		async fetchProblemAreaCodeByPostcode(body) {
			return cachedAPI({ name: "ProblemAreaCode", body });
		},
		async fetchProblemNewAreaCodeByPostcode(body) {
			return cachedAPI({ name: "ProblemNewAreaCode", body });
		},
		async fetchEngineersQueuesAddJobToAllocationQueue(body) {
			return cachedAPI({ name: "AllocationQueues", body });
		},
		async fetchResponseTimes() {
			return cachedAPI({ name: "ResponseTimes" });
		},
		async fetchSetBookingId(body) {
			return cachedAPI({ name: "SetBookingId", body });
		},
		async fetchSetupBooking(body) {
			return cachedAPI({ name: "SetupBooking", body });
		},
		async fetchInitialBooking(body) {
			return cachedAPI({ name: "InitialBooking", body });
		},
		async fetchTimeRequirementTypes(body) {
			return cachedAPI({ name: "TimeRequirementsTypes", body });
		},
		async fetchTradeGroupsMap(body) {
			return cachedAPI({ name: "TradeGroupsMap", body });
		},
		async fetchUsersStatistics(body) {
			return cachedAPI({ name: "GetUsersStatistics", body });
		},
		async fetchTradeTypes(body) {
			return cachedAPI({ name: "TradeTypes", body });
		},
		async fetchEvents(body) {
			return cachedAPI({ name: "Events", body });
		},
		async fetchEventsByCallId(body) {
			return cachedAPI({ name: "EventsByCallId", body });
		},
		async fetchCallHistoryBySource(body) {
			return cachedAPI({ name: "CallHistory", body });
		},
		async fetchUpdateBooking(body) {
			return cachedAPI({ name: "UpdateBooking", body });
		},
		async fetchCompletedJobsByJobId(body) {
			return cachedAPI({ name: "CompletedJobs", body });
		},
		async fetchJobByJobId(body) {
			return cachedAPI({ name: "JobById", body });
		},
		async fetchJobReviewsByJobId(body) {
			return cachedAPI({ name: "JobReviews", body });
		},
		async fetchGetSetting(body) {
			return cachedAPI({ name: "GetSetting", body });
		},
		async fetchGetCharges(body) {
			return cachedAPI({ name: "GetCharges", body });
		},
		async fetchEnquiriesQueue(body) {
			return cachedAPI({ name: "GetEnquiriesQueue", body });
		},
		async fetchGetScript(body) {
			return cachedAPI({ name: "GetScript", body });
		},
		async fetchServiceAgreementsAvailability(body) {
			return cachedAPI({ name: "GetServiceAgreementsAvailability", body });
		},
		async fetchServiceAgreementsValidateCampaignCode(body) {
			return cachedAPI({
				name: "GetServiceAgreementsValidateCampaignCode",
				body,
			});
		},
		async fetchServiceAgreementsGetPlanCostAfterOffers(body) {
			return cachedAPI({
				name: "GetServiceAgreementsGetPlanCostAfterOffers",
				body,
			});
		},
		async fetchServiceAgreementsGetPlanCostAfterOffers_v2(body) {
			return cachedAPI({
				name: "GetServiceAgreementsGetPlanCostAfterOffersV2",
				body,
			});
		},
		async fetchPaymentMethods(body) {
			return cachedAPI({ name: "GetPaymentMethods", body });
		},
		async fetchAddressBlacklist(body) {
			return cachedAPI({ name: "CheckAddressBlacklist", body });
		},
		async addressSearch(body) {
			return cachedAPI({ name: "AddressSearch", body });
		},
		async jobsSearch(body) {
			return cachedAPI({ name: "JobsSearch", body });
		},
		async jobDetails(body) {
			return cachedAPI({ name: "JobDetails", body });
		},
		async fetchJobScripts() {
			return cachedAPI({ name: "JobScripts" });
		},
		async fetchChargingTableBaseVAT() {
			return cachedAPI({ name: "ChargingTableBaseVAT" });
		},
		async fetchChargingTablePrices() {
			return cachedAPI({ name: "ChargingTablePrices" });
		},
		async fetchChargingTablePriceAdjustments() {
			return cachedAPI({ name: "ChargingTablePriceAdjustments" });
		},
		async fetchChargingTableBase() {
			return cachedAPI({ name: "ChargingTableBase" });
		},
		async fetchChargingTableScheduleGroupSchedules() {
			return cachedAPI({ name: "ChargingTableScheduleGroupSchedules" });
		},
		async fetchChargingTableScheduleGroups() {
			return cachedAPI({ name: "ChargingTableScheduleGroups" });
		},
		async fetchChargingTableAccountCodes() {
			return cachedAPI({ name: "ChargingTableAccountCodes" });
		},
		async fetchChargingTableAccountTypes() {
			return cachedAPI({ name: "ChargingTableAccountTypes" });
		},
		async fetchChargingTablePostcodes() {
			return cachedAPI({ name: "ChargingTablePostcodes" });
		},
		async fetchPostcodeAreas() {
			return cachedAPI({ name: "PostcodeAreas" });
		},
		async fetchTop5Calls(body) {
			return cachedAPI({ name: "Top5Calls", body });
		},
		async fetchGetCurrentCall(body) {
			return cachedAPI({ name: "GetCurrentCall", body });
		},
		async fetchGetCurrentCallFive9(
			callId: number,
			agentId: number,
			options?: any,
		) {
			return cachedAPI({
				name: "GetCurrentCallFive9",
				body: { callId, agentId },
				...(options && { path: options }),
			});
		},
		async fetchGetCurrCallFive9(callId: number, agentId: number) {
			return API.storedProceduresCallsGetCurrentCallFive9(callId, agentId);
		},
		async bookingDataGetAll() {
			return cachedAPI({ name: "BookingDataGetAll" });
		},
		async bookingDataGet(id) {
			return cachedAPI({ name: "BookingDataGet", id });
		},
		async bookingDataPost(body) {
			return cachedAPI({ name: "BookingDataPost", body });
		},
		async bookingDataDelete(id) {
			return cachedAPI({ name: "BookingDataDelete", id });
		},
		async bookingDataPatch(id, body) {
			return cachedAPI({ name: "BookingDataPatch", id, body });
		},
		async bookingDataGetAllVersions() {
			return cachedAPI({ name: "BookingDataGetAllVersions" });
		},
		async fetchBoilerManufacturers() {
			return cachedAPI({ name: "GetBoilerManufacturers" });
		},
		async fetchHeatingTypes() {
			return cachedAPI({ name: "GetHeatingTypes" });
		},
		async fetchBuildingTypes() {
			return cachedAPI({ name: "GetBuildingTypes" });
		},
		async fetchOfferAllocationReasons() {
			return cachedAPI({ name: "GetOfferAllocationReasons" });
		},
		async fetchPaymentTypes() {
			return cachedAPI({ name: "GetPaymentTypes" });
		},
		async fetchGlobalSettings() {
			return cachedAPI({ name: "GlobalSettings" });
		},
		async fetchOtherPhoneCalls() {
			return cachedAPI({ name: "OtherPhoneCalls" });
		},
		async fetchSetCall(body) {
			return cachedAPI({ name: "SetCall", body });
		},
		async fetchDataProviderSetData(body) {
			return cachedAPI({ name: "SetData", body });
		},
		async fetchGetCallMeBackQueue(body) {
			return cachedAPI({ name: "GetCallMeBackQueue", body });
		},

		async fetchGetCallMeBack(body) {
			return cachedAPI({ name: "GetCallMeBack", body });
		},
		async fetchGetCallMeBackNotes(body) {
			return cachedAPI({ name: "GetCallMeBackNotes", body });
		},
		async fetchCallMeBackSendSMS(body) {
			return cachedAPI({ name: "CallMeBackSendSMS", body });
		},
		async fetchCallMeBackSetCalled(body) {
			return cachedAPI({ name: "CallMeBackSetCalled", body });
		},
		async fetchCallMeBackSendEmail(body) {
			return cachedAPI({ name: "CallMeBackSendEmail", body });
		},
		async fetchGetCallMeSetFollowUp(body) {
			return cachedAPI({ name: "GetCallMeSetFollowUp", body });
		},
		async fetchServiceAgreementType(body) {
			return cachedAPI({ name: "GetServiceAgreementType", body });
		},
		async fetchServiceAgreementTypes(body) {
			return cachedAPI({ name: "GetServiceAgreementTypes", body });
		},
		async fetchIncentiveTypes(body) {
			return cachedAPI({ name: "GetIncentiveTypes", body });
		},
		async fetchIncentive(body) {
			return cachedAPI({ name: "GetIncentive", body });
		},
		async fetchAvailableOffers(body) {
			return cachedAPI({ name: "GetAvailableOffers", body });
		},
		async fetchAvailableOffers_v2(body) {
			return cachedAPI({ name: "GetAvailableOffersV2", body });
		},
		async fetchBenefitsTypes(body) {
			return cachedAPI({ name: "GetBenefitsTypes", body });
		},
		async fetchSendRequisition(body) {
			return cachedAPI({ name: "SetRequisition", body });
		},
		async fetchSetServiceAgreementV4(body) {
			return cachedAPI({ name: "SetServiceAgreementV4", body });
		},
		async fetchSetServiceAgreementV5(body) {
			return cachedAPI({ name: "SetServiceAgreementV5", body });
		},
		async fetchAutoAllocation(body) {
			return cachedAPI({ name: "GetAutoAllocation", body });
		},
		async fetchCallMeBacksSetAsBooked(body) {
			return cachedAPI({ name: "CallMeBacksSetAsBooked", body });
		},
		async fetchJobsPatch(id, body) {
			return cachedAPI({ name: "PatchJob", id, body });
		},
		async fetchCallsPatch(id, body) {
			return cachedAPI({ name: "PatchCalls", id, body });
		},
		async fetchGetCallMeBackLock(body) {
			return cachedAPI({ name: "GetCallMeBackLock", body });
		},
		async fetchGetCallMeBackUnlock(body) {
			return cachedAPI({ name: "GetCallMeBackUnlock", body });
		},
		async fetchGetCallMeBackCancel(body) {
			return cachedAPI({ name: "GetCallMeBackCancel", body });
		},
		async fetchSetCallMeBackLock(body) {
			return cachedAPI({ name: "SetCallMeBackLock", body });
		},
		async jobCompletion(body) {
			return cachedAPI({ name: "JobCompletion", body });
		},
		async fetchCallByJobId(body) {
			return cachedAPI({ name: "CallByJobId", body });
		},
		async fetchCallTypesSp(body) {
			return cachedAPI({ name: "CallTypesSp", body });
		},
		async apiLogsDelete(id) {
			return cachedAPI({ name: "ApiLogsDelete", id });
		},
		async apiLogsGetAll() {
			return cachedAPI({ name: "ApiLogs" });
		},

		async apiLogsGet(id) {
			return cachedAPI({ name: "ApiLogsById", id });
		},
		async apiLogsPost(body) {
			return cachedAPI({ name: "ApiLogsCreate", body });
		},
		async apiLogsPostMany(body) {
			return cachedAPI({ name: "ApiLogsCreateMany", body });
		},
		async generalLogsGetAll() {
			return cachedAPI({ name: "GeneralLogs" });
		},
		async generalLogsGet(id) {
			return cachedAPI({ name: "GeneralLogsById", id });
		},
		async generalLogsPost(body) {
			return cachedAPI({ name: "GeneralLogsCreate", body });
		},
		async generalLogsPostMany(body) {
			return cachedAPI({ name: "GeneralLogsCreateMany", body });
		},
		async generalLogsDelete(id) {
			return cachedAPI({ name: "GeneralLogsDelete", id });
		},
		async setGoCardlessBankAccount(body) {
			return cachedAPI({ name: "GoCardlessBankAccount", body });
		},
		async setGoCardlessCustomer(body) {
			return cachedAPI({ name: "GoCardlessCustomer", body });
		},
		async setServiceAgreementCustomer(body) {
			return cachedAPI({ name: "SetServiceAgreementCustomer", body });
		},
	};
};

// define window global API
// declare global
declare global {
	interface Window {
		APIdb: APIInterface.APIObject;
	}
}
window.APIdb = api();
